/**
 * Every mode a room may be declared in, in one list.
 *
 * `ROOM_DEVICE_RULES`' arrangement exactly, one field along on the same record, and for its
 * reason: three readers need the same list — the zod enum in `schemas/rooms.ts`, the console's
 * mode selector on the roster, and whatever a lobby row draws to say which kind of room somebody
 * is about to press Play on. Three copies would be three lists to forget to extend, and the
 * schema's would be the one that mattered: a mode the console could offer and the server would
 * refuse.
 *
 * `classic` leads it because it is the default, and the default is the upgrade path — a
 * `room_roster` row written before this field existed reads forward as one. See `RoomMode` for
 * why a mode is fixed for the life of a room.
 */

import type { RoomMode } from '../types/index.js';

/** Every value `RoomSettings.mode` may take. The zod enum and the console read this. */
export const ROOM_MODES = [
  'classic',
  'deathmatch',
  'teams',
  'zombie',
] as const satisfies readonly RoomMode[];

/** What each mode is called on screen. */
export const ROOM_MODE_LABELS: Record<RoomMode, string> = {
  classic: 'Classic',
  deathmatch: 'Deathmatch',
  teams: 'Teams',
  zombie: 'Zombie',
};

/**
 * What each mode actually does, in one sentence, for the caption under the console's selector.
 *
 * Here rather than in the console for `roomAccessBadges`' reason: the sentence an operator reads
 * before they tick a box is the same sentence a later surface would want beside the badge, and a
 * second copy of it is a second copy to leave behind when the mode changes.
 */
export const ROOM_MODE_DESCRIPTIONS: Record<RoomMode, string> = {
  classic:
    'Players join at any moment, grow, are eaten and respawn. The room never starts and never ' +
    'ends.',
  deathmatch:
    'The room plays rounds: it gathers a lobby, spawns everybody at once, and the last one ' +
    'standing wins it. A player who arrives mid-round waits for the next one.',
  teams:
    'Rounds played red against blue. The lobby is split down the middle, you may only swallow ' +
    'the other colour, and the round goes to the team left standing — or, when the clock runs ' +
    'out, to the team holding the most mass between them. Needs an even seat count.',
  zombie:
    'Red against blue, but being eaten does not end your run — it changes your side. Your last ' +
    'circle going down turns you the colour of whoever took it, and you play on for them. The ' +
    'round runs until one side has swallowed everybody. Needs an even seat count.',
};
